import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, Notification } from '../hooks/useNotifications';
import AlertDetailModal from './alerts/AlertDetailModal';
import { api, endpoints } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

interface NotificationCenterProps {
  className?: string;
}

// Memoized icon getter - defined outside component for performance
const getNotificationIcon = (type: Notification['type']) => {
  return null;
};

// Memoized priority color getter
const getPriorityColor = (priority: Notification['priority']) => {
  switch (priority) {
    case 'urgent':
      return 'border-l-red-600 bg-red-50';
    case 'high':
      return 'border-l-orange-600 bg-orange-50';
    case 'medium':
      return 'border-l-yellow-600 bg-yellow-50';
    case 'low':
      return 'border-l-blue-600 bg-blue-50';
    default:
      return 'border-l-gray-600 bg-gray-50';
  }
};

// Time formatter - pure function, can be memoized
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const {
    notifications,
    allNotifications,
    unreadCount,
    preferences,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    refetchAll,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const previousUnreadCount = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const navigateToActionUrl = useCallback((actionUrl: string) => {
    // Defense-in-depth: only allow same-origin HTTP(S) paths for in-app navigation.
    if (!actionUrl || actionUrl.startsWith('//')) {
      return;
    }

    if (actionUrl.startsWith('/')) {
      navigate(actionUrl);
      return;
    }

    try {
      const parsedUrl = new URL(actionUrl, window.location.origin);
      const isHttp = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
      if (!isHttp || parsedUrl.origin !== window.location.origin) {
        return;
      }

      navigate(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
    } catch {
      // Ignore malformed action URLs.
    }
  }, [navigate]);

  // Listen for external open events (e.g., from Dashboard)
  useEffect(() => {
    const handleOpenNotifications = () => {
      setIsOpen(true);
      setShowAll(true);
    };

    window.addEventListener('openNotifications' as any, handleOpenNotifications);
    document.addEventListener('openNotifications', handleOpenNotifications);

    return () => {
      window.removeEventListener('openNotifications' as any, handleOpenNotifications);
      document.removeEventListener('openNotifications', handleOpenNotifications);
    };
  }, []);

  // Fetch all notifications when dropdown opens
  useEffect(() => {
    if (isOpen && showAll) {
      refetchAll();
    }
  }, [isOpen, showAll, refetchAll]);

  // Play sound when new notifications arrive
  useEffect(() => {
    if (unreadCount > previousUnreadCount.current && unreadCount > 0) {
      // Play notification sound
      try {
        const audio = new Audio('data:audio/wav;base64,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');
        audio.volume = 0.3;
        audio.play().catch(() => {
          // Ignore errors if audio play fails (user might not have interacted yet)
        });
      } catch (e) {
        // Ignore audio errors
      }
    }
    previousUnreadCount.current = unreadCount;
  }, [unreadCount]);

  const handleNotificationClick = useCallback((notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // If action_url is an alert URL, open alert modal instead of navigating
    if (notification.action_url && notification.action_url.includes('/alerts/')) {
      const alertId = notification.action_url.split('/alerts/')[1];
      // Fetch alert details and open modal
      api.get(endpoints.alert(alertId))
        .then(response => {
          setSelectedAlert(response.data);
          setIsOpen(false);
        })
        .catch(() => {
          // If alert fetch fails, try to navigate
          if (notification.action_url) {
            navigateToActionUrl(notification.action_url);
          }
        });
    } else if (notification.action_url) {
      navigateToActionUrl(notification.action_url);
    }
  }, [markAsRead, navigateToActionUrl]);

  const displayNotifications = useMemo(() => {
    return showAll ? allNotifications : notifications;
  }, [showAll, allNotifications, notifications]);

  return (
    <div className={`relative ${className}`}>
      {/* Notification Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && showAll) {
            refetchAll();
          }
        }}
        className="relative p-2 text-white hover:text-wood-50 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Notifications"
      >
        <span>🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white ring-2 ring-wood-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded shadow-lg border-2 border-grape-700 z-50 max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-1">
                {!showSettings && (
                  <>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      aria-label="Settings"
                    >
                      <span>⚙</span>
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      aria-label="Close"
                    >
                      <span>✕</span>
                    </button>
                  </>
                )}
                {showSettings && (
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    aria-label="Back"
                  >
                    <span>✕</span>
                  </button>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && preferences && (
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                <h4 className="font-medium text-gray-900">Notification Preferences</h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.email_enabled}
                      onChange={(e) => updatePreferences({ email_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Email notifications</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.in_app_enabled}
                      onChange={(e) => updatePreferences({ in_app_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">In-app notifications</span>
                  </label>
                </div>
              </div>
            )}

            {/* Notifications List */}
            {!showSettings && (
              <>
                {/* Actions */}
                {displayNotifications.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
                    <button
                      onClick={() => setShowAll(!showAll)}
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {showAll ? 'Show unread only' : 'Show all'}
                    </button>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => {
                          markAllAsRead();
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center space-x-1"
                      >
                        <span>Mark all as read</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Notifications */}
                <div className="overflow-y-auto flex-1">
                  {isLoading ? (
                    <div className="p-8 text-center text-gray-500">
                      <LoadingSpinner size="md" className="text-indigo-600 mx-auto" />
                      <p className="mt-2 text-sm">Loading notifications...</p>
                    </div>
                  ) : displayNotifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p className="text-sm">No notifications</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-300">
                      {displayNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer border-l-4 ${getPriorityColor(notification.priority)} ${
                            notification.is_read ? 'opacity-75' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleNotificationClick(notification);
                            }
                          }}
                          aria-label={`Notification: ${notification.title}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5" aria-hidden="true">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className={`text-sm font-medium ${notification.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
                                    {notification.title}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {notification.message}
                                  </p>
                                  <div className="flex items-center space-x-2 mt-2">
                                    <span className="text-xs text-gray-500">
                                      {formatTime(notification.created_at)}
                                    </span>
                                    {notification.priority === 'urgent' && (
                                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                                        Urgent
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1 ml-2">
                                  {!notification.is_read && (
                                    <div className="h-2 w-2 bg-indigo-600 rounded-full"></div>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteNotification(notification.id);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                                    aria-label="Delete notification"
                                  >
                                    <span>✕</span>
                                  </button>
                                </div>
                              </div>
                              {notification.action_url && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNotificationClick(notification);
                                  }}
                                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                  {notification.action_label || 'View details'} →
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
      
      {/* Alert Detail Modal */}
      {selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onResolve={() => {
            refetchAll();
          }}
        />
      )}
    </div>
  );
};

export default NotificationCenter;

